import React, { PropsWithChildren } from "react";
import { useTranslation } from "i18n";
import { Pill } from "../../../../Atoms/Pill/Pill";
import GenericAppShell, { GenericAppShellProps } from "../GenericAppShell";
import LeftHandNavItem from "../LeftHandNavItem";
import NavItem from "../NavItem";
import NavSearch from "../NavSearch";
import QuickActionButton from "../QuickActionButton";
import { ThemeContextValues } from "../NavThemeContext";
import { ReactComponent as StarIcon } from "../../../../../images/svg/icons/star-outline.svg";
import { ReactComponent as SearchIcon } from "../../../../../images/svg/icons/search.svg";
import { ReactComponent as UserIcon } from "../../../../../images/svg/icons/user-outline.svg";
import { ReactComponent as MegaphoneIcon } from "../../../../../images/svg/icons/megaphone.svg";
import { ReactComponent as CogIcon } from "../../../../../images/svg/icons/cog-settings.svg";
import { ReactComponent as HelpIcon } from "../../../../../images/svg/icons/help-question.svg";

const AgentAppShell = ({
  footerComponent,
  hideFooter,
  children,
}: PropsWithChildren<
  Pick<GenericAppShellProps, "hideFooter" | "footerComponent">
>) => {
  const { t } = useTranslation();

  return (
    <GenericAppShell
      theme={ThemeContextValues.Agent}
      hideFooter={hideFooter}
      footerComponent={footerComponent}
      leftHandNavItems={
        <>
          <LeftHandNavItem to="/jobs/matching-my-filters" icon={SearchIcon}>
            {t("common:nav.links.jobsFeed.filters")}
          </LeftHandNavItem>
          <LeftHandNavItem to="/jobs/all-opportunities" isSubNav>
            {t("common:nav.links.projects")}
          </LeftHandNavItem>
          <LeftHandNavItem to="/jobs/client-alerts" isSubNav>
            {t("common:nav.links.clientAlerts")}
          </LeftHandNavItem>
          <LeftHandNavItem to="/jobs/self-tape-requests" isSubNav>
            {t("common:nav.links.mediaRequests")}
            <Pill className="bg-blue-sky-2 ml-1.5" theme="light" size="md">
              {t("common:nav.links.pill.beta")}
            </Pill>
          </LeftHandNavItem>
          <LeftHandNavItem to="/agentweb/feed/messages" isSubNav>
            {t("common:nav.links.messages")}
          </LeftHandNavItem>
          <LeftHandNavItem to="/settings/jobs-feed" isSubNav>
            {t("common:nav.links.jobsFeedSettings")}
          </LeftHandNavItem>

          <LeftHandNavItem to="/clients" icon={UserIcon}>
            {t("common:nav.links.clientList")}
          </LeftHandNavItem>
          <LeftHandNavItem to="/agentweb/EmailCvs" isSubNav>
            {t("common:nav.links.emailClientCvs")}
          </LeftHandNavItem>
          <LeftHandNavItem to="/searchsuggestions" isSubNav>
            {t("common:nav.links.searchSuggestions")}
          </LeftHandNavItem>
          <LeftHandNavItem to="/agentweb/Clients/PinList/filtered" isSubNav>
            {t("common:nav.links.clientPins")}
          </LeftHandNavItem>
          <LeftHandNavItem to="/clientaccess" isSubNav>
            {t("common:nav.links.clientAccessLevel")}
          </LeftHandNavItem>
          <LeftHandNavItem to="clients/yp-application" isSubNav>
            {t("common:nav.links.youngPerformerApplications")}
          </LeftHandNavItem>

          <LeftHandNavItem to="/clients/seeking-representation" icon={StarIcon}>
            {t("common:nav.links.seekingRepresentation")}
          </LeftHandNavItem>
          <LeftHandNavItem to="https://spotlight-showcase.com" isSubNav>
            {t("common:nav.links.digitalShowcase")}
          </LeftHandNavItem>
          <LeftHandNavItem to="https://calendar.spotlight.com" isSubNav>
            {t("common:nav.links.gradPerformanceCalendar")}
          </LeftHandNavItem>

          <LeftHandNavItem
            to="https://www.spotlight.com/help-and-faqs/links-for-professionals-on-spotlight/"
            icon={MegaphoneIcon}
          >
            {t("common:nav.links.industryResources")}
          </LeftHandNavItem>
          <LeftHandNavItem
            to="https://www.spotlight.com/news-and-advice/"
            isSubNav
          >
            {t("common:nav.links.newsAndAdvice")}
          </LeftHandNavItem>
          <LeftHandNavItem to="https://app.spotlight.com/contacts" isSubNav>
            {t("common:nav.links.contactsListings")}
          </LeftHandNavItem>
          <LeftHandNavItem
            to="https://www.spotlight.com/members/spotlight-performer-events/"
            isSubNav
          >
            {t("common:nav.links.clientEvents")}
          </LeftHandNavItem>
        </>
      }
      dynamicHeaderContent={
        <>
          <NavSearch
            submitSearch={(searchTerm) =>
              window.location.replace(`/jobs/performers/search/${searchTerm}`)
            }
          />
          <QuickActionButton
            label={t("common:nav.links.jobsFeed")}
            linkUrl="/jobs/matching-my-filters"
          />
        </>
      }
      userMenuItems={
        <>
          <NavItem to="/settings" icon={CogIcon}>
            {t("common:nav.links.accountSettings")}
          </NavItem>
          <NavItem
            to="https://portal.spotlight.com/portal/account/details"
            isSubNav
          >
            {t("common:nav.links.agentUserDetails")}
          </NavItem>
          <NavItem to="/clients/agency" isSubNav>
            {t("common:nav.links.agencyDetails")}
          </NavItem>

          <NavItem
            to="https://www.spotlight.com/the-small-print/help-for-agents-using-the-spotlight-link/"
            icon={HelpIcon}
          >
            {t("common:nav.links.helpAndTutorials")}
          </NavItem>
          <NavItem to="https://support.spotlight.com/hc/en-us/" isSubNav>
            {t("common:nav.links.support")}
          </NavItem>
          <NavItem
            to="https://www.spotlight.com/help-and-faqs/a-guide-to-spotlight-for-agents-onboarding-new-clients/"
            isSubNav
          >
            {t("common:nav.links.clientOnboarding")}
          </NavItem>
          <NavItem
            to="https://www.spotlight.com/the-small-print/terms-conditions/"
            isSubNav
          >
            {t("common:nav.links.agent.termsAndConditions")}
          </NavItem>
          <NavItem to="https://www.spotlight.com/rooms-and-studios/" isSubNav>
            {t("common:nav.links.hireRoomsStudios")}
          </NavItem>
        </>
      }
    >
      {children}
    </GenericAppShell>
  );
};

export default AgentAppShell;
